import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { normalize, schema } from 'normalizr';
import fetchAllParticipants from 'network/fetchAllParticipants';
import fetchParticipantsByCriteria from 'network/fetchParticipantsByCriteria';
import fetchParticipantsByRole from 'network/fetchParticipantsByRole';
import concat from 'lodash/concat';
import { APP_DOMAIN } from 'appenv';

export const participantEntity = new schema.Entity('participants');

export const fetchParticipantListByCriteria = createAsyncThunk(
  'participants/fetchByCriteria',
  async (_, { getState }) => {
    const state = getState();
    const { lastCursor, hasNextPage: nextPageStatus } = state.participants;
    const { participantFilter } = state;
    const filterOptions = participantFilter.get('queryFilters').toJS();
    try {
      const {
        totalCount,
        pageInfo: {
          endCursor,
          hasNextPage,
        },
        edges,
      } = await fetchParticipantsByCriteria('', 20, lastCursor, filterOptions, APP_DOMAIN);
      const participants = edges.map(({ cursor, node }) => {
        const id = node.uuid;
        return {
          ...node,
          id,
          cursor,
        };
      });
      const normalized = normalize(participants, [participantEntity]);
      return {
        entities: normalized.entities,
        lastCursor: endCursor,
        hasNextPage,
        totalCount,
      };
    } catch (error) {
      console.error(error);
    }
    return {
      lastCursor,
      hasNextPage: nextPageStatus,
      entities: {},
      totalCount: 0,
    };
  },
  {
    condition: (_, { getState }) => {
      const { loading } = getState().participants;
      return !loading;
    },
  },
);

export const fetchNonAttendeeParticipantsByCriteria = createAsyncThunk(
  'participants/fetchByCriteria',
  async (_, { getState }) => {
    const state = getState();
    const { lastCursor, hasNextPage: nextPageStatus } = state.participants;
    const { participantFilter } = state;
    const filterOptions = participantFilter.get('queryFilters').toJS();
    try {
      const {
        totalCount,
        pageInfo: {
          endCursor,
          hasNextPage,
        },
        edges,
      } = await fetchParticipantsByCriteria('', 20, lastCursor, filterOptions, APP_DOMAIN, ['exhibitor', 'organizer']);
      const participants = edges.map(({ cursor, node }) => {
        const id = node.uuid;
        return {
          ...node,
          id,
          cursor,
        };
      });
      const normalized = normalize(participants, [participantEntity]);
      return {
        entities: normalized.entities,
        lastCursor: endCursor,
        hasNextPage,
        totalCount,
      };
    } catch (error) {
      console.error(error);
    }
    return {
      lastCursor,
      hasNextPage: nextPageStatus,
      entities: {},
      totalCount: 0,
    };
  },
  {
    condition: (_, { getState }) => {
      const { loading } = getState().participants;
      return !loading;
    },
  },
);

export const fetchParticipantList = createAsyncThunk(
  'participants/fetchAll',
  async (_, { getState }) => {
    const { lastCursor, hasNextPage: nextPageStatus } = getState().participants;
    try {
      const {
        pageInfo: {
          endCursor,
          hasNextPage,
        },
        edges,
      } = await fetchAllParticipants(lastCursor);
      const participants = edges.map(({ cursor, node }) => {
        const id = node.uuid;
        return {
          ...node,
          id,
          cursor,
        };
      });
      const normalized = normalize(participants, [participantEntity]);
      return {
        entities: normalized.entities,
        lastCursor: endCursor,
        hasNextPage,
      };
    } catch (error) {
      console.error(error);
    }
    return {
      lastCursor,
      hasNextPage: nextPageStatus,
      entities: {},
    };
  },
  {
    condition: (_, { getState }) => {
      const { loading } = getState().participants;
      return !loading;
    },
  },
);

export const fetchNonAttendeeParticipants = createAsyncThunk(
  'participants/fetchAll',
  async (_, { getState }) => {
    const { lastCursor } = getState().participants;
    try {
      const { edges: orgEdges } = await fetchParticipantsByRole('organizer', lastCursor);
      const {
        pageInfo: {
          endCursor,
          hasNextPage,
        },
        edges,
      } = await fetchParticipantsByRole('exhibitor', lastCursor);
      const participants = concat(orgEdges, edges).map(({ cursor, node }) => {
        const id = node.uuid;
        return {
          ...node,
          id,
          cursor,
        };
      });
      const normalized = normalize(participants, [participantEntity]);
      return {
        entities: normalized.entities,
        lastCursor: endCursor,
        hasNextPage,
      };
    } catch (error) {
      console.error(error);
    }
    return {
      entities: {},
    };
  },
  {
    condition: (_, { getState }) => {
      const { loading } = getState().participants;
      return !loading;
    },
  },
);

const participantsAdapter = createEntityAdapter();

export const {
  selectById: selectParticipantsById,
  selectIds: selectParticipantIds,
  selectEntities: selectParticipantEntities,
  selectAll: selectAllParticipants,
  selectTotal: selectTotalParticipants,
} = participantsAdapter.getSelectors((state) => state.participants);

const initialState = {
  ...participantsAdapter.getInitialState(),
  lastCursor: undefined,
  initialLoading: true,
  hasNextPage: true,
  loading: false,
  totalCount: 0,
};

export const ParticipantsSlice = createSlice({
  name: 'participants',
  initialState,
  reducers: {
    resetParticipants() {
      return initialState;
    },
  },
  extraReducers: {
    [fetchParticipantList.pending]: (state) => {
      state.loading = true;
    },
    [fetchParticipantList.fulfilled]: (state, action) => {
      const results = action.payload.entities.participants || {};
      participantsAdapter.upsertMany(state, results);
      state.lastCursor = action.payload.lastCursor;
      state.hasNextPage = action.payload.hasNextPage;
      state.initialLoading = false;
      state.loading = false;
    },
    [fetchNonAttendeeParticipants.pending]: (state) => {
      state.loading = true;
    },
    [fetchNonAttendeeParticipants.fulfilled]: (state, action) => {
      const results = action.payload.entities.participants || {};
      participantsAdapter.upsertMany(state, results);
      state.lastCursor = action.payload.lastCursor;
      state.hasNextPage = action.payload.hasNextPage;
      state.initialLoading = false;
      state.loading = false;
    },
    [fetchParticipantListByCriteria.pending]: (state) => {
      state.loading = true;
    },
    [fetchParticipantListByCriteria.fulfilled]: (state, action) => {
      const results = action.payload.entities.participants || {};
      participantsAdapter.upsertMany(state, results);
      state.lastCursor = action.payload.lastCursor;
      state.hasNextPage = action.payload.hasNextPage;
      state.totalCount = action.payload.totalCount;
      state.initialLoading = false;
      state.loading = false;
    },
    [fetchNonAttendeeParticipantsByCriteria.pending]: (state) => {
      state.loading = true;
    },
    [fetchNonAttendeeParticipantsByCriteria.fulfilled]: (state, action) => {
      const results = action.payload.entities.participants || {};
      participantsAdapter.upsertMany(state, results);
      state.lastCursor = action.payload.lastCursor;
      state.hasNextPage = action.payload.hasNextPage;
      state.totalCount = action.payload.totalCount;
      state.initialLoading = false;
      state.loading = false;
    },
  },
});

export const {
  resetParticipants,
} = ParticipantsSlice.actions;

export default ParticipantsSlice.reducer;
